// Whether this browser is in step with the server (docs/64): the kit's Data/SyncStatus.swift (SyncStatusRule) is the
// reference, and cases/sync-status.json holds the two together. Firestore keeps working offline by design: it reads
// from its cache and queues writes, so the app looks the same in step and hours behind. The cloud in the list's bar
// says the difference, only when it matters: after the browser has been behind for two minutes in a row.

import { addDays, dayOf, dayParts, timeOf } from "./days";

/** How long a device may be behind before the cloud shows (ms): it keeps it from flashing at launch or on waking. */
export const SYNC_WAIT_MS = 2 * 60_000;
/** The Watch's wait for reading (it syncs now and then by design); the web listens, as the iPhone does. */
export const WATCH_SYNC_WAIT_MS = 15 * 60_000;

/** What the sync knows about being in step (the kit's `SyncObservation`); moments in ms since 1970. */
export interface SyncObservation {
  /** Whether the browser says there is a network (`navigator.onLine`). */
  online: boolean;
  /** Since when reading has been behind: listeners that haven't answered from the server, answer from the cache, or failed. */
  readingBehindSince?: number;
  /** Since when the oldest write of this browser has waited for the server. */
  writeWaitingSince?: number;
  /** The last moment this browser was in step, kept with its copy of the account. */
  inStepAt?: number;
  /** How long reading may be behind before it shows (default `SYNC_WAIT_MS`). */
  readingWait?: number;
}

export type SyncStatusKind = "notSynced" | "offline" | "update";

/** What the cloud says while it shows: the kind, and the last moment in step. */
export interface SyncStatus {
  kind: SyncStatusKind;
  since: number;
}

/** Everything read from the server and nothing waiting for it. */
export function isInStep(observation: SyncObservation): boolean {
  return observation.online && observation.readingBehindSince === undefined && observation.writeWaitingSince === undefined;
}

/**
 * What the cloud says at `now`; null while it doesn't show (SyncStatusRule.status). Below the account's minimum it says
 * so at once. A waiting write counts after `SYNC_WAIT_MS`, reading after its own wait.
 */
export function syncStatus(observation: SyncObservation, blocked: boolean, now: number): SyncStatus | null {
  const wait = observation.readingWait ?? SYNC_WAIT_MS;
  const reading = observation.readingBehindSince;
  const writing = observation.writeWaitingSince;
  const behind = (reading !== undefined && now - reading >= wait) || (writing !== undefined && now - writing >= SYNC_WAIT_MS);
  const started = [reading, writing].filter((value): value is number => value !== undefined);
  const since = observation.inStepAt ?? (started.length ? Math.min(...started) : now);
  if (blocked) return { kind: "update", since };
  if (!behind) return null;
  return { kind: observation.online ? "notSynced" : "offline", since };
}

/** When the status may change by itself (a wait running out); null once one ran out, or when nothing waits. */
export function nextSyncChange(observation: SyncObservation, now: number): number | null {
  const ends: number[] = [];
  if (observation.readingBehindSince !== undefined) ends.push(observation.readingBehindSince + (observation.readingWait ?? SYNC_WAIT_MS));
  if (observation.writeWaitingSince !== undefined) ends.push(observation.writeWaitingSince + SYNC_WAIT_MS);
  if (ends.length === 0 || ends.some((end) => end <= now)) return null;
  return Math.min(...ends);
}

/** How a moment is said, by its day in the zone (SyncStatusRule.Moment). */
export type SyncMoment =
  | { kind: "today"; hour: number; minute: number }
  | { kind: "yesterday"; hour: number; minute: number }
  | { kind: "thisYear"; month: number; day: number }
  | { kind: "earlier"; year: number; month: number; day: number };

export function syncMoment(moment: number, now: number, zone: string): SyncMoment {
  const day = dayOf(new Date(moment), zone);
  const today = dayOf(new Date(now), zone);
  const [hour, minute] = timeOf(new Date(moment), zone).split(":").map(Number) as [number, number];
  if (day === today) return { kind: "today", hour, minute };
  if (day === addDays(today, -1)) return { kind: "yesterday", hour, minute };
  const parts = dayParts(day);
  if (parts.year === dayParts(today).year) return { kind: "thisYear", month: parts.month, day: parts.date };
  return { kind: "earlier", year: parts.year, month: parts.month, day: parts.date };
}
